import { useEffect, useRef } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { clientRoutes } from '@/ApiPaths';

const RELOADED_FOR_KEY = 'app-update-reloaded-for';

/** True when this page load was itself a reload (F5 or location.reload()). */
function loadedByReload(): boolean {
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return nav?.type === 'reload';
}

/** Reload at most once per deployed build: if we already reloaded for this id and still
 *  don't run it, something between us and the server is inconsistent, so stop trying. */
function reloadForBuild(buildId: string) {
  try {
    if (sessionStorage.getItem(RELOADED_FOR_KEY) === buildId) return;
    sessionStorage.setItem(RELOADED_FOR_KEY, buildId);
  } catch {
    // storage unavailable: reload anyway, the check only runs on user navigation/focus
  }
  window.location.reload();
}

/** Returns the deployed build id (dist/version.json, a static file served by nginx) if it
 *  differs from the one compiled into this tab, otherwise null. */
async function newerDeployedBuild(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const { buildId } = (await res.json()) as { buildId?: unknown };
    return typeof buildId === 'string' && buildId !== __APP_BUILD_ID__ ? buildId : null;
  } catch {
    return null;
  }
}

/**
 * Moves open tabs onto the newest deployed build without background polling:
 * - on every in-app navigation: check /version.json; if a newer build is live,
 *   reload right away (the user just left the previous page, nothing is lost)
 * - when the tab becomes visible again: check; if newer, show a notice and
 *   reload on the next navigation (never mid-form)
 * - if a lazily loaded file of the old build is missing (deleted by a deploy),
 *   reload once immediately
 * Embedded viewers (/embed/:id on partner sites) only get the last rule: they are
 * short-lived page views and must not show notices or reload on their own.
 *
 * Loop safety: nothing runs on initial page load; a build-id reload happens at most
 * once per build id; a missing-file reload never happens on a page that was itself
 * loaded by a reload.
 */
export function AppUpdateWatcher() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const updatePending = useRef<string | null>(null);
  const lastPathname = useRef(pathname);
  const isEmbed = matchPath(clientRoutes.embed, pathname) !== null;

  useEffect(() => {
    if (import.meta.env.DEV || isEmbed) return;
    // A freshly loaded page always has the current build; only check on real navigations.
    if (lastPathname.current === pathname) return;
    lastPathname.current = pathname;
    if (updatePending.current) {
      reloadForBuild(updatePending.current);
      return;
    }
    let cancelled = false;
    void newerDeployedBuild().then((newer) => {
      if (newer && !cancelled) reloadForBuild(newer);
    });
    return () => {
      cancelled = true;
    };
  }, [pathname, isEmbed]);

  useEffect(() => {
    if (import.meta.env.DEV || isEmbed) return;
    const onVisible = async () => {
      if (document.visibilityState !== 'visible' || updatePending.current) return;
      const newer = await newerDeployedBuild();
      if (!newer) return;
      updatePending.current = newer;
      notifications.show({
        id: 'app-update',
        title: t('appUpdate.title'),
        message: (
          <>
            <Text size="sm">{t('appUpdate.message')}</Text>
            <Button size="xs" variant="light" mt="xs" onClick={() => window.location.reload()}>
              {t('appUpdate.reload')}
            </Button>
          </>
        ),
        autoClose: false,
        withCloseButton: false,
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [t, isEmbed]);

  useEffect(() => {
    const onPreloadError = (event: Event) => {
      if (loadedByReload()) return; // already reloaded once, let the error surface
      event.preventDefault();
      window.location.reload();
    };
    window.addEventListener('vite:preloadError', onPreloadError);
    return () => window.removeEventListener('vite:preloadError', onPreloadError);
  }, []);

  return null;
}
